import { SurveyPostData } from '@surveycake/logic-engine';
import { useEngineInstance } from '@surveycake/react-logic-engine';
import { Wording } from '@surveycake/wording/survey';
import { submit, SubmitResponseData, deleteTempAnswer } from 'app/core/http';
import { PageService, PageType } from 'app/core/page';
import {
  AlertStatus,
  Exception,
  ExceptionService,
  ExceptionStatus
} from 'app/core/status';
import { SurveyConfig, useSurveyConfig } from 'app/core/survey-config';
import { useTracker } from 'app/core/tracker';
import { Quarantine } from 'app/modules/quarantine';
import {
  disableBeforeunload,
  handleEngineValidation
} from 'app/modules/survey';
import { t } from 'app/shared/i18n';
import { modalConfirm } from 'app/shared/utils/modalConfirm';
import { isInIframe, tempAnswerEnable } from 'config/system';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import URI from 'urijs';
import { useOnSubmit } from './useOnSubmit';

function redirectAfterSubmit(
  { redirect, fileHash }: SubmitResponseData,
  redirectUrl: string,
  setException: Dispatch<SetStateAction<Exception | undefined>>,
  setPage: Dispatch<SetStateAction<PageType>>,
  surveyConfig: SurveyConfig
) {
  if (redirect === 'fulled') {
    setException(new Exception('FULLED'));
  } else if (redirectUrl && fileHash) {
    const resolvedRedirectUrl = new URI(redirectUrl);
    const queryLanguage = surveyConfig.getQueryLanguage();

    resolvedRedirectUrl.addQuery('svid', surveyConfig.surveyHash);
    resolvedRedirectUrl.addQuery('hash', fileHash);

    if (queryLanguage) {
      resolvedRedirectUrl.addQuery('lang', queryLanguage);
    }

    disableBeforeunload();
    window.location.href = resolvedRedirectUrl.toString();
  } else {
    setPage(redirect);
  }
}

function sendSubmitWindowMessageToParent() {
  if (isInIframe) {
    window.parent.postMessage({ type: 'SUBMIT_SUCCESS' }, '*');
  }
}

function isExceptionMessage(
  message: keyof typeof ExceptionStatus | keyof typeof AlertStatus
): message is keyof typeof ExceptionStatus {
  return message in ExceptionStatus;
}

function alertSubmitError(message: keyof typeof AlertStatus, wording: Wording) {
  let resolvedMessage = '';

  if (message === 'MISSED_ANSWER') {
    resolvedMessage = wording.common.alert.missed_answer;
  } else if (message === 'INVALID_PARAMS') {
    resolvedMessage = wording.common.alert.invalid_params;
  } else if (message === 'TOKEN_ERROR') {
    resolvedMessage = wording.common.alert.token_error;
  } else if (message === 'UPLOAD_FOLDER_FULL') {
    resolvedMessage = wording.common.alert.upload_folder_full;
  } else if (message === 'SAVING_FAILED') {
    resolvedMessage = wording.common.alert.saving_failed;
  } else if (message === 'NO_AUTHED') {
    resolvedMessage = wording.common.alert.no_authed;
  } else if (message === 'RECAPTCHA_FAILED') {
    resolvedMessage = wording.common.alert.recaptcha_failed;
  }

  if (resolvedMessage) {
    modalConfirm(resolvedMessage, { confirmText: 'OK' });
  }
}

function handleSubmitErrorMessage(
  message: keyof typeof ExceptionStatus | keyof typeof AlertStatus,
  setException: Dispatch<SetStateAction<Exception | undefined>>,
  wording: Wording
) {
  if (isExceptionMessage(message)) {
    setException(new Exception(message));
  } else {
    alertSubmitError(message, wording);
  }
}

function resolvePostData(
  postData: SurveyPostData,
  surveyConfig: SurveyConfig
): SurveyPostData {
  const shouldAddMbrIsoType =
    Quarantine.isEnabled(surveyConfig) &&
    Quarantine.isSameBrowserFlagInCookie(surveyConfig);

  return {
    ...postData,
    ansobj: {
      ...postData.ansobj,
      ...(shouldAddMbrIsoType && { mbr_iso_type: 'SAME_BROWSER' })
    } as SurveyPostData['ansobj']
  };
}

export function usePostAnswer() {
  const [posting, setPosting] = useState(false);
  const [showButton, setShowButton] = useState(true);
  const setException = ExceptionService.useSetException();
  const setPage = PageService.useSetPage();
  const surveyConfig = useSurveyConfig();
  const { wording, surveyHash, tempAnswerHash } = surveyConfig;
  const tracker = useTracker();
  const postAnswerRequest = useCallback(
    async (postData: SurveyPostData, redirectUrl: string) => {
      setPosting(true);
      try {
        const responseData = await submit(postData);
        setShowButton(false);
        const callback = () => {
          sendSubmitWindowMessageToParent();

          if (Quarantine.isEnabled(surveyConfig)) {
            Quarantine.setSameBrowserFlagToCookie(surveyConfig);
          }

          tracker.trackSubmitSuccess('index');
          redirectAfterSubmit(
            responseData,
            redirectUrl,
            setException,
            setPage,
            surveyConfig,
          );
        };

        if (tempAnswerHash && tempAnswerEnable) {
          await deleteTempAnswer(
            surveyHash,
            tempAnswerHash,
            callback,
          );
        } else {
          callback();
        }
      } catch (error) {
        handleSubmitErrorMessage(error.message, setException, wording);
        setPosting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setException, setPage, surveyConfig, wording]
  );
  const engine = useEngineInstance();
  const postAnswer = useCallback(() => {
    const result = engine.getPostAnswer();

    if (!result) {
      return;
    }

    const { post, validation, redirect_url: redirectUrl } = result;
    handleEngineValidation(validation, () => {
      if (!surveyConfig.isPreview()) {
        postAnswerRequest(resolvePostData(post, surveyConfig), redirectUrl);
      } else if (surveyConfig.getQueryThemeName()) {
        modalConfirm(wording.common.preview.no_submit, { confirmText: 'OK' });
      } else {
        modalConfirm(
          t(wording.common.preview.redirect_to_ending, {
            pagename: post.ansobj.svend
          }),
          {
            cancelText: 'CANCEL',
            confirmText: 'OK',
            onConfirm: () => setPage(post.ansobj.svend)
          }
        );
      }
    });
  }, [engine, surveyConfig, wording, setPage, postAnswerRequest]);
  const { onSubmit, disabled } = useOnSubmit(postAnswer);

  return [onSubmit, disabled, posting, showButton] as [() => void, boolean, boolean, boolean];
}
